import React, { useCallback, useContext, useState } from "react";
import { useLocation } from "react-router-dom";

import { faArrowRight, faPlus, faSlidersH } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { ActionItemsList } from "~/actionItems";
import ArchiveActionItemsBanner from "~/actionItems/ArchiveActionItemsBanner";
import { Views } from "~/actionItems/constants";
import { GROUP_BY_NAMES, GroupByOption, type GroupByType } from "~/actionItems/filters/GroupBy";
import { SORT_BY_NAMES, SortByOption, type SortByType } from "~/actionItems/filters/SortBy";
import { ActionItemsContext, ActionItemsState } from "~/actionItems/newActionItems/ActionItemsContext";
import { inNewCompanionMode } from "~/lib/utils";
import { useRealAiActionItemsIsOn } from "~/meetingRecorder/utils";
import { Button, LinkButton } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import panelStyles from "~/ui/CollapsiblePanel/CollapsiblePanel.module.css";

import styles from "./ActionItems.module.css";

function ActionItemsNavHeader() {
	const { onCreateActionItem } = useContext(ActionItemsContext);
	const onClickCreateActionItem = useCallback(() => onCreateActionItem(), [onCreateActionItem]);

	return (
		<header className={styles.panelHeader}>
			<h1>Action items</h1>
			<IconButton
				icon={faPlus}
				title="New action item"
				className={styles.headerNewItemButton}
				onClick={onClickCreateActionItem}
			/>
		</header>
	);
}

export function ActionItemsPanel() {
	const location = useLocation();
	const isOnActionItemsPage = location.pathname.startsWith("/action-items");

	return (
		<section className={cx(styles.panel, panelStyles.panelBase, "nav-panel")}>
			<ActionItemsState>
				<ActionItemsNavHeader />
				<PanelActionItemsFilters />
				{!isOnActionItemsPage && !inNewCompanionMode && (
					<LinkButton variant="secondary" size="slim" to="/action-items/" className={styles.fullViewButton}>
						Open full view&ensp;
						<FontAwesomeIcon icon={faArrowRight} />
					</LinkButton>
				)}
				<ArchiveActionItemsBanner isPanel={true} />
				<div className={styles.main}>
					<ActionItemsList />
				</div>
			</ActionItemsState>
		</section>
	);
}

const userViewTitle = "Assigned to me";
const teammatesViewTitle = "Assigned to others";

export function PanelActionItemsFilters() {
	const { filters, onUserView, onTeammatesView, view } = useContext(ActionItemsContext);
	const {
		groupBy,
		sortBy,
		showCompleted,
		onToggleShowCompleted,
		showWontDo,
		onToggleShowWontDo,
		showAIDetected,
		onToggleShowAIDetected,
	} = filters;

	const [assignedToMenuIsOpen, setAssignedToMenuIsOpen] = useState<boolean>(false);
	const [filterMenuIsOpen, setFilterMenuIsOpen] = useState<boolean>(false);

	const toggleAssignedToMenuOpen = useCallback(
		() => setAssignedToMenuIsOpen(assignedToMenuIsOpen => !assignedToMenuIsOpen),
		[],
	);
	const toggleFilterMenuOpen = useCallback(() => setFilterMenuIsOpen(filterMenuIsOpen => !filterMenuIsOpen), []);

	const currentViewSelectionTitle = view === Views.TEAMMATES ? teammatesViewTitle : userViewTitle;

	const isRealActionItemsFeatureFlagOn = useRealAiActionItemsIsOn();

	return (
		<div className={styles.filters}>
			<Menu open={assignedToMenuIsOpen} onOpenChange={toggleAssignedToMenuOpen}>
				<Menu.Trigger>
					<Button
						variant="secondary"
						size="slim"
						className={cx({ [styles.activeTriggerButton]: assignedToMenuIsOpen })}
					>
						{currentViewSelectionTitle}&ensp;
						<FontAwesomeIcon icon={assignedToMenuIsOpen ? faCaretUp : faCaretDown} />
					</Button>
				</Menu.Trigger>
				<Menu.Content>
					<Menu.Item onSelect={onUserView}>{userViewTitle}</Menu.Item>
					<Menu.Item onSelect={onTeammatesView}>{teammatesViewTitle}</Menu.Item>
				</Menu.Content>
			</Menu>
			<Menu open={filterMenuIsOpen} onOpenChange={toggleFilterMenuOpen}>
				<Menu.Trigger>
					<Button variant="secondary" size="slim" className={cx({ [styles.activeTriggerButton]: filterMenuIsOpen })}>
						<FontAwesomeIcon icon={faSlidersH} />
						&ensp;
						<FontAwesomeIcon icon={filterMenuIsOpen ? faCaretUp : faCaretDown} />
					</Button>
				</Menu.Trigger>
				<Menu.Portal>
					<Menu.Content>
						<Menu.Sub>
							<Menu.SubTrigger>Group by</Menu.SubTrigger>
							<Menu.Portal>
								<Menu.SubContent>
									<Menu.Label>Grouping</Menu.Label>
									<Menu.RadioGroup value={groupBy}>
										{Object.keys(GROUP_BY_NAMES).map(groupByType => (
											<GroupByOption key={groupByType} type={groupByType as GroupByType} />
										))}
									</Menu.RadioGroup>
								</Menu.SubContent>
							</Menu.Portal>
						</Menu.Sub>
						<Menu.Sub>
							<Menu.SubTrigger>Sort</Menu.SubTrigger>
							<Menu.Portal>
								<Menu.SubContent>
									<Menu.Label>Sort</Menu.Label>
									<Menu.RadioGroup value={sortBy}>
										{Object.keys(SORT_BY_NAMES).map(sortByType => (
											<SortByOption key={sortByType} type={sortByType as SortByType} />
										))}
									</Menu.RadioGroup>
								</Menu.SubContent>
							</Menu.Portal>
						</Menu.Sub>
						<Menu.Sub>
							<Menu.SubTrigger>Filter</Menu.SubTrigger>
							<Menu.Portal>
								<Menu.SubContent>
									<Menu.SwitchItem checked={showCompleted} onCheckedChange={onToggleShowCompleted}>
										Show completed
									</Menu.SwitchItem>
									<Menu.SwitchItem checked={showWontDo} onCheckedChange={onToggleShowWontDo}>
										Show archived
									</Menu.SwitchItem>
									{isRealActionItemsFeatureFlagOn && (
										<Menu.SwitchItem checked={showAIDetected} onCheckedChange={onToggleShowAIDetected}>
											Show AI-Detected
										</Menu.SwitchItem>
									)}
								</Menu.SubContent>
							</Menu.Portal>
						</Menu.Sub>
					</Menu.Content>
				</Menu.Portal>
			</Menu>
		</div>
	);
}
